import { useCheckoutTracking } from '@checkout/core'
import { forwardRef, useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'

import styles from './CurrencySelector.module.css'
import { CheckIcon, ChevronIcon } from '../../icons'

import type { CurrencyOption } from '@checkout/core'

export interface CurrencySelectorProps {
  /** Available currencies */
  currencies: CurrencyOption[]
  /** Currently selected currency code */
  value: string
  /** Callback when currency changes */
  onChange: (currencyCode: string) => void
  /** Additional class name */
  className?: string
}

/**
 * CurrencySelector - Dropdown for selecting currency
 * Figma reference: node 490-2277
 *
 * Uses React Portal to render menu to body to avoid overflow clipping
 */
export const CurrencySelector = forwardRef<HTMLDivElement, CurrencySelectorProps>(
  ({ currencies, value, onChange, className }, ref) => {
    const { trackEvent } = useCheckoutTracking()
    const [isOpen, setIsOpen] = useState(false)
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 })
    const dropdownRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLUListElement>(null)

    const selectedCurrency = currencies.find((c) => c.code === value) || currencies[0]

    // Calculate menu position based on trigger button
    const updateMenuPosition = useCallback(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        setMenuPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
        })
      }
    }, [])

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node
        // Check if click is outside both trigger and menu
        if (
          triggerRef.current &&
          !triggerRef.current.contains(target) &&
          menuRef.current &&
          !menuRef.current.contains(target)
        ) {
          setIsOpen(false)
        }
      }

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside)
      }
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen])

    // Close on escape key
    useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsOpen(false)
        }
      }

      if (isOpen) {
        document.addEventListener('keydown', handleEscape)
      }

      return () => {
        document.removeEventListener('keydown', handleEscape)
      }
    }, [isOpen])

    // Update position on scroll/resize when open
    useEffect(() => {
      if (!isOpen) return

      const handleUpdate = () => updateMenuPosition()
      window.addEventListener('scroll', handleUpdate, true)
      window.addEventListener('resize', handleUpdate)
      return () => {
        window.removeEventListener('scroll', handleUpdate, true)
        window.removeEventListener('resize', handleUpdate)
      }
    }, [isOpen, updateMenuPosition])

    const handleToggle = useCallback(() => {
      if (!isOpen) {
        updateMenuPosition()
      }
      setIsOpen((prev) => !prev)
    }, [isOpen, updateMenuPosition])

    const handleSelect = (currencyCode: string) => {
      trackEvent('currency_change', 'order_summary', { currency: currencyCode })
      onChange(currencyCode)
      setIsOpen(false)
    }

    // Render menu in portal to body to avoid overflow clipping
    const menuContent = isOpen && (
      <ul
        ref={menuRef}
        className={styles.menu}
        role="listbox"
        style={{
          position: 'fixed',
          top: menuPosition.top,
          right: menuPosition.right,
        }}
      >
        {currencies.map((currency) => {
          const isSelected = currency.code === value
          return (
            <li key={currency.code}>
              <button
                type="button"
                className={`${styles.menuItem} ${isSelected ? styles.menuItemActive : ''}`}
                onClick={() => handleSelect(currency.code)}
                role="option"
                aria-selected={isSelected}
              >
                <span className={styles.menuItemLabel}>{currency.label}</span>
                {isSelected && (
                  <CheckIcon className={styles.menuItemCheck} width={16} height={16} />
                )}
              </button>
            </li>
          )
        })}
      </ul>
    )

    return (
      <div ref={ref} className={`${styles.wrapper} ${className ?? ''}`}>
        <div ref={dropdownRef} className={styles.dropdown}>
          <button
            ref={triggerRef}
            type="button"
            className={styles.trigger}
            onClick={handleToggle}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            data-ga-action-name="currency_selector_open"
            data-ga-module="order_summary"
          >
            <span className={styles.value}>{selectedCurrency?.code}</span>
            <ChevronIcon
              direction={isOpen ? 'up' : 'down'}
              className={styles.chevron}
              width={12}
              height={6}
            />
          </button>

          {/* Render menu in portal to body to avoid overflow clipping */}
          {menuContent && createPortal(menuContent, document.body)}
        </div>
      </div>
    )
  }
)

CurrencySelector.displayName = 'CurrencySelector'
